import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldAlert, StopCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * SupportAccessBanner — persistent, high-contrast banner shown at the
 * very top of the app whenever the current admin has an active
 * cross-tenant support grant. It exists so the admin is *never*
 * confused about which workspace they're looking at. Auto-refreshes
 * every 30s and self-hides when the grant expires or is ended.
 */
export default function SupportAccessBanner() {
    const { user, refresh } = useAuth();
    const [grant, setGrant] = useState(null);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (!user || user.role !== "admin") return undefined;
        const load = async () => {
            try {
                const { data } = await api.get("/admin/support-access/current");
                setGrant(data?.active || null);
            } catch { /* silent */ }
        };
        load();
        const poll = setInterval(load, 30000);
        return () => clearInterval(poll);
    }, [user]);

    // Countdown tick
    useEffect(() => {
        if (!grant) return undefined;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [grant]);

    if (!grant) return null;

    const remainingMs = Math.max(0, new Date(grant.expires_at).getTime() - now);
    if (remainingMs <= 0) {
        // Timer hit zero — clear locally and re-refresh /me so tenant pivot goes away
        setTimeout(() => { setGrant(null); refresh(); }, 100);
    }
    const m = Math.floor(remainingMs / 60000);
    const s = Math.floor((remainingMs % 60000) / 1000);

    const endNow = async () => {
        try {
            await api.post("/admin/support-access/end");
            toast.success("Support session ended.");
            setGrant(null);
            await refresh();
        } catch { toast.error("Could not end session."); }
    };

    return (
        <div
            className="w-full bg-amber-500 text-stone-900 border-b-2 border-amber-700 px-4 py-2.5 flex items-center justify-center gap-3 text-xs sm:text-sm shadow-lg z-50 print:hidden"
            data-testid="support-access-banner"
        >
            <ShieldAlert size={14} className="text-amber-900 shrink-0" />
            <div className="flex-1 min-w-0 text-center">
                <b className="uppercase tracking-wider text-[11px] sm:text-[12px]">Support mode</b>
                <span className="mx-2">·</span>
                Viewing <b>{grant.target_tenant_name || grant.tenant_id}</b>
                <span className="hidden sm:inline"> · &ldquo;{grant.reason}&rdquo;</span>
                <span className="mx-2">·</span>
                <span className="font-mono tabular-nums font-bold" data-testid="banner-countdown">
                    {m}:{s.toString().padStart(2, "0")}
                </span>
            </div>
            <button
                onClick={endNow}
                data-testid="banner-end-btn"
                className="inline-flex items-center gap-1 rounded bg-stone-900 text-white px-2.5 py-1 text-[11px] font-semibold hover:bg-stone-800"
            >
                <StopCircle size={11} /> End
            </button>
            <Link
                to="/admin/support-access"
                data-testid="banner-manage-link"
                className="text-[11px] font-semibold underline hover:no-underline shrink-0"
            >
                Manage
            </Link>
        </div>
    );
}
